import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableContainer,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import DescriptionIcon from "@material-ui/icons/Description";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { makeStyles } from "@material-ui/core/styles";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { cancelButton, saveButton } from "../../../../core/styles/ui.constants";
import { enviarCorreo } from "../../../../core/utils/email/correo";
import { generarHtmlCorreo } from "../../../../core/utils/email/correoTemplate";
import { ADMINISTRACION, ADMINISTRADOR, DIRECTOR } from "../../../../core/utils/roles.constants";
import { useLoading } from "../../../../contexts/LoadingContext";
import IngresarItemsModal from "./IngresarItemsModal";
import SelectorGenerico from "../../../../components/Selector/SelectorGenerico";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import ConfirmDialog from "../../../../components/Dialogs/ConfirmDialog";
import { useAuth } from "../../../../contexts/AuthContext";
import { CORREO_DIRECTOR } from "@/core/utils/constantes";
import {
  handleMultipleFiles,
  handleFileAccess,
  formatFileSize,
  ACCEPT_ADJUNTOS_INPUT,
  EXTENSIONES_ADJUNTOS_CONFIG,
  esExtensionPermitida,
} from "../../../../core/utils/file";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "1000px",
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  titleText: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formRowItem: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(2),
  },
  fullRow: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  buttonArea: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  footerButtons: {
    // display: "flex",
    // justifyContent: "flex-end",
    marginLeft: theme.spacing(2),
    // marginTop: theme.spacing(2),
  },
  areaBlanca: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
  },
  customTooltip: {
    fontSize: "0.8rem",
  },
  actionButton: saveButton,
  cancelButton: cancelButton,
  autocompleteInputRoot: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56, // Ajustá este valor a lo que necesites
    },
  },
}));

const CrearRequisicionModal = ({
  open,
  onClose,
  onSuccess,
  onUpdateEstado,
  requisicion,
  tipoRequisicion,
  usuarioLocal,
}) => {
  const classes = useStyles();
  const { loading, show, hide } = useLoading();
  const [openItemsModal, setOpenItemsModal] = useState(false);
  const [estado, setEstado] = useState("");
  const [sectors, setSectors] = useState({});
  // const [rubros, setRubros] = useState({});
  const [items, setItems] = useState([]);

  /**
   * ESTADO DE MÚLTIPLES ADJUNTOS:
   * - adjuntosExistentes: Archivos ya persistidos en el servidor (tabla `requisiciones_adjuntos`).
   *   Formato: [{ idAdjunto, path, nombreOriginal, tamano, mimeType }]
   * - archivosNuevos: Archivos locales seleccionados por el usuario en esta sesión (aún en memoria).
   *   Formato: [{ file: File, idTemp: string, nombreOriginal: string, tamano: number }]
   *
   * JUSTIFICACIÓN:
   * Mantener separados los existentes de los nuevos previene subir archivos innecesariamente
   * si el usuario cancela la edición o cierra el modal, evitando archivos huérfanos en disco.
   */
  const [adjuntosExistentes, setAdjuntosExistentes] = useState([]);
  const [archivosNuevos, setArchivosNuevos] = useState([]);

  const fileInputRef = useRef(null);
  const modalRef = useRef();
  const sectorAnteriorRef = useRef(null);

  const [openItems, setOpenItems] = useState(false);
  const [sectorSeleccionado, setSectorSeleccionado] = useState(null);

  const { getUser } = useAuth();
  const user = getUser();
  const userSectorIds = Array.isArray(user?.sectores) && user.sectores.length > 0
    ? user.sectores.map((sector) => String(sector))
    : user?.IdSector ? [String(user.IdSector)] : [];
  const canSelectAnySector = [ADMINISTRADOR, ADMINISTRACION].includes(Number(user?.es_admin));

  const formDefaultValues = {
    sector: (user?.sectores && user.sectores[0]) || user.IdSector || "",
    concepto: "",
    fechaCreacion: new Date().toISOString().split("T")[0],
    observaciones: "",
    tipoOrdenDeCompra: "",
  };

  const [sectorInicial, setSectorInicial] = useState(null);

  const [formData, setFormData] = useState(formDefaultValues);
  const [errors, setErrors] = useState({});

  const [editable, setEditable] = useState(false);
  const [requisicionOriginal, setRequisicionOriginal] = useState(null);

  const [itemModalData, setItemModalData] = useState(null);
  const [itemModalIndex, setItemModalIndex] = useState(null);
  const [itemModalReadOnly, setItemModalReadOnly] = useState(false);
  const [itemModalCanEnableEdit, setItemModalCanEnableEdit] = useState(false);
  const isAdminEditor = [ADMINISTRADOR, ADMINISTRACION].includes(Number(usuarioLocal?.es_admin));

  const convertirItems = (itemsOS) => {
    if (!Array.isArray(itemsOS)) return [];

    const agrupadosPorRubro = {};

    itemsOS.forEach((item) => {
      const idRubro = item.idRubro;
      if (!agrupadosPorRubro[idRubro]) {
        agrupadosPorRubro[idRubro] = {
          idRubro: String(idRubro),
          nombreRubro: item.nombreRubro || "Varios",
          descripcion: item.descripcion || "",
          informacionOS: [],
        };
      }

      agrupadosPorRubro[idRubro].informacionOS.push({
        // idItemsOS: item.idItemOS,
        selectedOs: item.idOS,
        selectedItem: item.idItems,
        itemName: item.itemName,
        importeUnitario: item.importeUnitario,
        cantidad: String(item.cantidad),
        // cantidadMaxima: 6
      });
    });

    return Object.values(agrupadosPorRubro);
  };

  /**
   * Extrae la lista uniforme de adjuntos tanto si la requisición proviene de la tabla
   * normalizada (`requisicion.adjuntos`) como si viene con el campo legacy (`requisicion.Adjunto`).
   */
  const extraerAdjuntosDeRequisicion = (req) => {
    if (!req) return [];

    if (Array.isArray(req.adjuntos) && req.adjuntos.length > 0) {
      return req.adjuntos.map((adj) => ({
        idAdjunto: adj.idAdjunto || null,
        path: adj.path,
        nombreOriginal: adj.nombreOriginal || adj.path.split("-").slice(2).join("-") || "archivo.pdf",
        tamano: adj.tamano || null,
        mimeType: adj.mimeType || "application/pdf",
      }));
    }

    if (req.Adjunto && typeof req.Adjunto === "string" && req.Adjunto.trim() !== "") {
      return [{
        idAdjunto: null,
        path: req.Adjunto,
        nombreOriginal: req.Adjunto.split("-").slice(2).join("-") || "archivo.pdf",
        tamano: null,
        mimeType: "application/pdf",
      }];
    }

    return [];
  };

  const setRequisicion = (requisicion, rubrosMap = {}) => {
    const sectorId = String(requisicion.IdSector || "");

    setSectorSeleccionado(sectorId);
    setSectorInicial(sectorId || null);

    setFormData({
      sector: sectorId,
      concepto: requisicion.Concepto,
      fechaCreacion: requisicion.Fecha_creacion,
      observaciones: requisicion.Observaciones,
      tipoOrdenDeCompra: "",
    });

    // Cargar lista de adjuntos existentes normalizada y limpiar pendientes
    setAdjuntosExistentes(extraerAdjuntosDeRequisicion(requisicion));
    setArchivosNuevos([]);

    const itemsConNombre = (Array.isArray(requisicion.ItemsRequisicion) ? requisicion.ItemsRequisicion : []).map(
      (item) => ({
        ...item,
        nombreRubro: rubrosMap[item.idRubro] || "Desconocido",
      })
    );

    setItems(convertirItems(itemsConNombre));

    setEstado(requisicion.IdEstado);
  };


  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        const [sectorResponse, rubroResponse] = await Promise.all([api.sectores.getAll(), api.rubros.getAll()]);
        const sectorMap = {};
        sectorResponse.forEach((sector) => {
          sectorMap[sector.IdSector] = sector.Nombre;
        });
        const rubroMap = {};
        rubroResponse.forEach((rubro) => {
          rubroMap[rubro.IdRubro] = rubro.Nombre;
        });
        setSectors(sectorMap);
        // setRubros(rubroMap);

        if (requisicion) {
          setRequisicion(requisicion, rubroMap);
          setRequisicionOriginal({ ...requisicion });
        } else {
          const userSectorId = String((user?.sectores && user.sectores[0]) || user?.IdSector || "");

          setSectorSeleccionado(userSectorId);
          setSectorInicial(userSectorId || null);
          setFormData({ ...formDefaultValues, sector: userSectorId });
          setEstado(1);
          setEditable(true);
        }
      };

      fetchData();

    } else {
      setErrors({});
      setSectorInicial(null);
      setSectorSeleccionado("");
      setFormData(formDefaultValues);
      setItems([]);
      setAdjuntosExistentes([]);
      setArchivosNuevos([]);
      // setMissingOsByRubro({});
      // setShowMissingOsError(false);
      setEditable(false);
    }
  }, [open]);

  // useEffect(() => {
  //   if (!showMissingOsError) return;

  //   const faltantesActualizados = obtenerItemsSinOS(items);
  //   setMissingOsByRubro(faltantesActualizados);

  //   if (Object.keys(faltantesActualizados).length === 0) {
  //     setShowMissingOsError(false);
  //   }
  // }, [items, showMissingOsError, obtenerItemsSinOS]);

  const handleNuevoClick = () => {
    if (sectorInicial !== null && sectorSeleccionado !== sectorInicial) {
      const confirmCambio = window.confirm(
        "El sector seleccionado es distinto al anterior. ¿Desea continuar?"
      );
      if (!confirmCambio) {
        setItems([]);
        return;
      }
    }

    // Si no hay sectorInicial, lo establecemos con el sector actual
    setSectorInicial(sectorSeleccionado);

    if (modalRef.current) {
      modalRef.current.resetFields();
    }
    setItemModalData(null);
    setItemModalIndex(null);
    setItemModalReadOnly(false);
    setOpenItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setOpenItemsModal(false);
    setItemModalData(null);
    setItemModalIndex(null);
    setItemModalReadOnly(false);
    setItemModalCanEnableEdit(false);
  };

  const handleAdjuntoClick = () => fileInputRef.current?.click();

  /**
   * Maneja la selección de uno o varios archivos desde el input nativo.
   *
   * CASOS BORDE Y VALIDACIONES:
   * 1. Valida extensiones en lista blanca permitida.
   * 2. Valida tamaño individual (máx 25 MB).
   * 3. No pisa archivos previamente seleccionados: acumula los válidos en `archivosNuevos`.
   * 4. Limpia el input (`input.value = ''`) para permitir seleccionar el mismo archivo
   *    si el usuario lo borra y lo vuelve a agregar.
   */
  const handleFileChange = (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    const nuevosValidos = [];
    for (const file of files) {
      if (!esExtensionPermitida(file.name)) {
        alert(`El archivo '${file.name}' tiene un formato no permitido. Formatos aceptados: ${EXTENSIONES_ADJUNTOS_CONFIG.join(", ")}`);
        continue;
      }
      if (file.size > 25 * 1024 * 1024) {
        alert(`El archivo '${file.name}' supera el límite permitido de 25 MB.`);
        continue;
      }

      nuevosValidos.push({
        file,
        idTemp: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        nombreOriginal: file.name,
        tamano: file.size,
      });
    }

    if (nuevosValidos.length > 0) {
      setArchivosNuevos((prev) => [...prev, ...nuevosValidos]);
    }
    input.value = "";
  };

  const [archivoAEliminar, setArchivoAEliminar] = useState(null);
  const [eliminandoArchivo, setEliminandoArchivo] = useState(false);

  /**
   * Abre el modal de confirmación antes de eliminar cualquier archivo.
   */
  const handleSolicitarEliminarArchivo = (adj) => {
    setArchivoAEliminar(adj);
  };

  /**
   * Elimina un archivo ya guardado en el servidor de la lista local en memoria.
   */
  const handleEliminarAdjuntoExistente = (index) => {
    setAdjuntosExistentes((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Quita un archivo recién seleccionado de la lista de pendientes antes de subirlo.
   */
  const handleEliminarArchivoNuevo = (index) => {
    setArchivosNuevos((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Ejecuta la confirmación del usuario para quitar un archivo de la requisición.
   * - Archivos nuevos en memoria (`esNuevo`): se descartan inmediatamente de la sesión.
   * - Archivos persistidos (`!esNuevo`): se quitan de la tabla visual y, al presionar
   *   "Guardar" / "Guardar edición", el servidor los elimina de la base de datos, los borra
   *   físicamente del disco (/uploads/) y registra la entrada en la auditoría (logs_sistema).
   */
  const handleConfirmarEliminarArchivo = () => {
    if (!archivoAEliminar) return;

    if (archivoAEliminar.esNuevo) {
      handleEliminarArchivoNuevo(archivoAEliminar.indexOriginal);
    } else {
      handleEliminarAdjuntoExistente(archivoAEliminar.indexOriginal);
    }
    setArchivoAEliminar(null);
  };

  /**
   * Permite visualizar tanto archivos remotos del servidor como archivos locales
   * seleccionados en la sesión actual que aún no se han subido.
   */
  const handleVerArchivo = (item) => {
    if (item.file) {
      // Archivo local en memoria: crear blob temporal seguro
      const url = URL.createObjectURL(item.file);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      return;
    }

    if (item.path) {
      // Archivo remoto persistido en el servidor
      handleFileAccess(item.path, item.nombreOriginal);
    }
  };

  const handleDeleteItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const handleSaveItem = (item) => {
    setItems((prevItems) => {
      if (itemModalIndex === null) {
        return [...prevItems, item];
      }

      return prevItems.map((prevItem, index) =>
        index === itemModalIndex ? item : prevItem
      );
    });
  };

  const handleVerDetalleItem = (item, index) => {
    const puedeEditarDetalle =
      // [1, 2].includes(Number(estado)) && [1, 2, 4, 5].includes(Number(usuarioLocal?.es_admin));
      [1, 2].includes(Number(estado));


    setItemModalData(item);
    setItemModalIndex(index);
    setItemModalReadOnly(true);
    setItemModalCanEnableEdit(puedeEditarDetalle);
    setOpenItemsModal(true);
  };

  const handleSectorChange = useCallback(
    (event, newValue) => {
      if (newValue && !canSelectAnySector && !userSectorIds.includes(String(newValue.value))) {
        return;
      }

      sectorAnteriorRef.current = sectorSeleccionado;

      if (newValue) {
        setSectorSeleccionado(newValue.value);
        setFormData({ ...formData, sector: newValue.value });

        // Si no hay sectorInicial, lo fijamos acá
        // if (sectorInicial === null) {
        setSectorInicial(newValue.value);
        // }
      } else {
        setSectorSeleccionado("");
        setFormData({ ...formData, sector: "" });
      }
      setOpenItems(false);
    },
    [canSelectAnySector, formData, sectorSeleccionado, userSectorIds]
  );

  const validarDatos = () => {
    const newErrors = {};
    if (!formData.sector) newErrors.sector = "El sector es obligatorio.";
    if (
      formData.sector &&
      !canSelectAnySector &&
      !userSectorIds.includes(String(formData.sector))
    ) {
      newErrors.sector = "Solo puede seleccionar sectores asignados a su usuario.";
    }
    if (!formData.concepto) newErrors.concepto = "El concepto es obligatorio.";
    if (!formData.fechaCreacion)
      newErrors.fechaCreacion = "La fecha de creación es obligatoria.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGuardar = async (estado) => {
    const validacion = validarDatos();
    if (!validacion) return;

    show("Guardando, por favor espere...");

    try {
      const currentUserId = Number(usuarioLocal?.IdUsuario || usuarioLocal?.id || 0);

      if (!currentUserId) {
        throw new Error("No se pudo identificar el usuario logueado para crear la requisición.");
      }

      // 1. Subida concurrente de todos los archivos nuevos en memoria
      let adjuntosSubidosNuevos = [];
      if (archivosNuevos.length > 0) {
        show("Subiendo archivos adjuntos...");
        try {
          adjuntosSubidosNuevos = await handleMultipleFiles(archivosNuevos.map((item) => item.file));
        } catch (uploadError) {
          hide();
          alert(`Error al subir archivos: ${uploadError.message}`);
          return;
        }
      }

      // 2. Unificar adjuntos existentes que no fueron eliminados + los recién subidos
      const adjuntosFinales = [
        ...adjuntosExistentes.map((adj) => ({
          path: adj.path,
          nombreOriginal: adj.nombreOriginal,
          tamano: adj.tamano || null,
          mimeType: adj.mimeType || "application/pdf",
        })),
        ...adjuntosSubidosNuevos,
      ];

      const primerAdjuntoPath = adjuntosFinales.length > 0 ? adjuntosFinales[0].path : null;

      const requisicionPayload = {
        IdSector: Number(formData.sector),
        Concepto: formData.concepto,
        Fecha_creacion: formData.fechaCreacion,
        Observaciones: formData.observaciones,
        IdEstado: estado,
        IdUsuario: currentUserId,
        requisionOculto: 0,
        adjuntos: adjuntosFinales, // Lista normalizada para la tabla requisiciones_adjuntos
        Adjunto: primerAdjuntoPath, // Mantenido para retrocompatibilidad con la columna legacy
      };

      if (requisicion?.idRequision) {
        const idRequision = requisicion.idRequision;

        // Actualizar requisición principal
        await api.requisiciones.update(idRequision, requisicionPayload);

        // if (requisicion.ItemsRequisicion) {
        // Eliminar items no existentes
        await eliminarItemsNoExistentes(requisicion.ItemsRequisicion, items);
        // }

        // Crear nuevos items
        await crearNuevosItems(idRequision, items);

        const requisicionActualizada = {
          ...requisicionPayload,
          idRequision,
          NroRequisicion: requisicion.NroRequisicion,
          Fecha: requisicionPayload.Fecha_creacion,
          Sector: sectors[requisicionPayload.IdSector] || "",
        };

        onSuccess(requisicionActualizada);
      } else {
        const itemsPayload = items.flatMap((rubro) =>
          rubro.informacionOS
            .filter((info) => !info.idRequisionItem)
            .map((info) => ({
              idOS: info.selectedOs || null,
              idRubro: parseInt(rubro.idRubro),
              itemName: info.itemName,
              cantidad: info.cantidad,
              importeUnitario: info.importeUnitario,
              descripcion: rubro.descripcion,
            }))
        );

        const resRequisicion = await api.requisiciones.create({
          ...requisicionPayload,
          items: itemsPayload,
        });
        const idRequision = resRequisicion.idRequision;
        const nroRequisicion = resRequisicion.NroRequisicion;

        if (estado === 2) {
          const usuarios = await api.usuarios.getAll();
          const correosNotificacion = [
            ...new Set(
              usuarios
                .filter((u) =>
                  [DIRECTOR, ADMINISTRACION].includes(Number(u.es_admin))
                )
                .map((u) => (u.email || "").trim())
                .filter(Boolean),
              CORREO_DIRECTOR
            ),
          ];

          const html = generarHtmlCorreo({
            titulo: "Se cargaron nuevas requisiciones",
            encabezado: "Se cargaron nuevas requisiciones",
            cuerpo: `<p>La requisición con Número <strong>${nroRequisicion}</strong> se ha creado y necesita su autorización.</p>`,
            botonTexto: "Ver Requisición",
          });

          if (correosNotificacion.length > 0) {
            await enviarCorreo(
              correosNotificacion,
              "Se cargaron nuevas requisiciones",
              html
            );
          }
        }

        const nuevaRequisicion = {
          ...requisicionPayload,
          idRequision,
          NroRequisicion: nroRequisicion,
          Fecha: requisicionPayload.Fecha_creacion,
          Sector: sectors[requisicionPayload.IdSector] || "",
          requisionOculto: 0,
        };

        onSuccess(nuevaRequisicion);
      }

      // onClose();
      close();
    } catch (error) {
      console.error("Error al guardar la requisición:", error.message);
      alert(error.message || "Ocurrió un error al guardar. Intenta nuevamente.");
    } finally {
      // setTimeout(() => setIsSubmitting(false), 2000);
      hide();
    }
  };

  const close = () => {
    setFormData(formDefaultValues);
    setErrors({});
    setSectorInicial(null);
    setItems([]);
    setAdjuntosExistentes([]);
    setArchivosNuevos([]);
    // setMissingOsByRubro({});
    // setShowMissingOsError(false);
    onClose();
  };

  // Función para eliminar items no existentes
  const eliminarItemsNoExistentes = async (itemsExistentes, items) => {
    const idsExistentes = itemsExistentes.map((item) => item.idRequisionItem);
    const idsActuales = items.flatMap((rubro) =>
      rubro.informacionOS.map((info) => info.idRequisionItem)
    );
    const idsAEliminar = idsExistentes.filter(
      (id) => !idsActuales.includes(id)
    );

    if (idsAEliminar.length > 0) {
      for (const id of idsAEliminar) {
        await api.itemsRequisiciones.delete(id);
      }
    }
  };

  // Función para crear nuevos items
  const crearNuevosItems = async (idRequision, items) => {
    const nuevosPayloads = items.flatMap((rubro) =>
      rubro.informacionOS
        .filter((info) => !info.idRequisionItem)
        .map((info) =>
          api.itemsRequisiciones.create({
            idRequision,
            // idItems: info.selectedItem,
            idOS: info.selectedOs || null,
            idRubro: parseInt(rubro.idRubro),
            itemName: info.itemName,
            cantidad: info.cantidad,
            importeUnitario: info.importeUnitario,
            descripcion: rubro.descripcion,
          })
        )
    );

    if (nuevosPayloads.length > 0) {
      await Promise.all(nuevosPayloads);
    }
  };

  const generarNroOrdenDeCompra = (idOrdenCompra) => {
    const año = new Date().getUTCFullYear();
    const idFormateado = idOrdenCompra.toString().padStart(4, "0");
    return `OC${año}0${idFormateado}`;
  };

  const crearOrdenCompraDesdeRequisicion = async (idRequision) => {
    if (!formData.tipoOrdenDeCompra) {
      throw new Error("Debe seleccionar si la orden de compra es Caja Chica o Expediente.");
    }

    const ordenesCompraPayload = {
      IdRequisicion: idRequision,
      IdSector: Number(formData.sector),
      Fecha_Creacion: new Date().toISOString().split("T")[0],
      Concepto: formData.concepto,
      Observaciones: formData.observaciones,
      IdEstado: 1,
      tipoOrdenDeCompra: formData.tipoOrdenDeCompra,
      oculto: 0,
    };

    const resOrdenCompra = await api.ordenesCompra.create(ordenesCompraPayload);
    const idOrdenCompra = resOrdenCompra.IdOC;

    await api.ordenesCompra.update(idOrdenCompra, {
      NroOrdenDeCompra: generarNroOrdenDeCompra(idOrdenCompra),
    });
  };

  const handleUpdateEstado = async (estado) => {
    try {
      const idRequision = requisicion.idRequision;

      // if (estado === 3) {
      //   const faltantes = obtenerItemsSinOS(items);

      //   if (Object.keys(faltantes).length > 0) {
      //     setMissingOsByRubro(faltantes);
      //     setShowMissingOsError(true);
      //     return;
      //   }

      //   setMissingOsByRubro({});
      //   setShowMissingOsError(false);
      // }

      await api.requisiciones.update(idRequision, {
        IdEstado: estado,
      });

      // Crear OC automáticamente si se aprueba la requisición
      if (estado === 3) {
        await crearOrdenCompraDesdeRequisicion(idRequision);
      }

      onUpdateEstado(estado);
    } catch (error) {
      console.error("Error al actualizar el estado:", error.message);
    }
  };

  const estadoBloqueado = estado === 3 || estado === 4;
  const isRequester =
    Number(usuarioLocal?.IdUsuario || usuarioLocal?.id || 0) ===
    Number(requisicion?.IdUsuario || requisicion?.idUsuario || 0);
  const canEnableApprovalEdit = Boolean(requisicion) && estado === 2 && (isAdminEditor || isRequester);
  const requiereHabilitarEdicion = Boolean(requisicion) && estado === 2;
  const puedeEditar = !estadoBloqueado && (!requiereHabilitarEdicion || editable);
  const puedeAprobar = estado === 2 && !editable && [ADMINISTRADOR, DIRECTOR].includes(Number(usuarioLocal?.es_admin));
  // const showErrorColumn = showMissingOsError && Object.keys(missingOsByRubro).length > 0;

  // Unificación de adjuntos existentes (persistidos) y nuevos (pendientes) para la tabla
  const todosLosAdjuntos = [
    ...adjuntosExistentes.map((adj, index) => ({
      ...adj,
      esNuevo: false,
      indexOriginal: index,
    })),
    ...archivosNuevos.map((nuevo, index) => ({
      ...nuevo,
      esNuevo: true,
      indexOriginal: index,
    })),
  ];

  return (
    <Dialog open={open} onClose={close} maxWidth="xl">
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.titleText}>
          {/* Crear Requisición */}
          {tipoRequisicion}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={close}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {requisicion && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {/* {estado === 2 && [1, 2, 5].includes(usuarioLocal?.es_admin) && ( */}
            {canEnableApprovalEdit && (
              <Tooltip
                title={
                  <span style={{ fontSize: "0.8rem" }}>
                    {editable ? "Cancelar edición" : "Habilitar edición"}
                  </span>
                }
              >
                <IconButton
                  onClick={() => {
                    if (editable) {
                      setFormData({
                        sector: requisicionOriginal.IdSector,
                        concepto: requisicionOriginal.Concepto,
                        fechaCreacion: requisicionOriginal.Fecha_creacion,
                        observaciones: requisicionOriginal.Observaciones,
                      });
                      // Restaurar lista original de adjuntos y descartar los seleccionados localmente
                      setAdjuntosExistentes(extraerAdjuntosDeRequisicion(requisicionOriginal));
                      setArchivosNuevos([]);

                      setItems(
                        convertirItems(
                          Array.isArray(requisicionOriginal.ItemsRequisicion)
                            ? requisicionOriginal.ItemsRequisicion
                            : []
                        )
                      );
                    }
                    setEditable(!editable);
                  }}
                >
                  {editable ? <CloseIcon /> : <EditIcon />}
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
        {/* Primera fila */}
        <div className={classes.formRow}>
          {/* Sector */}
          <div style={{ flex: 1 }}>
            <SelectorGenerico
              autoCompleteStyle={classes.autocompleteInputRoot}
              label="Sector"
              classNameDiv={classes.formRowItem}
              value={sectorSeleccionado}
              onChange={handleSectorChange}
              onHighlightChange={(event, newHighlight) => {
                // setHighlightedIndex(newHighlight);
              }}
              options={Object.entries(sectors).map(([key, label]) => ({
                value: key,
                label,
                disabled: !canSelectAnySector && !userSectorIds.includes(String(key)),
              }))}
              open={openItems}
              onOpen={() => setOpenItems(true)}
              onClose={() => setOpenItems(false)}
              error={!!errors.sector}
              getOptionDisabled={(option) => Boolean(option?.disabled)}
              disabled={Boolean(requisicion) || !puedeEditar}
            />
            {errors.sector && (
              <Typography color="error" variant="body2">
                {errors.sector}
              </Typography>
            )}
          </div>

          {/* Concepto */}
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1">Concepto</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={formData.concepto}
              error={!!errors.concepto || undefined}
              onChange={(e) =>
                setFormData({ ...formData, concepto: e.target.value })
              }
              disabled={!puedeEditar}
            />
            {errors.concepto && (
              <Typography color="error" variant="body2">
                {errors.concepto}
              </Typography>
            )}
          </div>

          {/* Fecha de creación */}
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1">Fecha de Creación</Typography>
            <TextField
              variant="outlined"
              type="date"
              fullWidth
              value={formData.fechaCreacion}
              error={!!errors.fechaCreacion || undefined}
              onChange={(e) =>
                setFormData({ ...formData, fechaCreacion: e.target.value })
              }
              disabled={!puedeEditar}
            />
            {errors.fechaCreacion && (
              <Typography color="error" variant="body2">
                {errors.fechaCreacion}
              </Typography>
            )}
          </div>
        </div>

        {/* Segunda fila */}
        <div className={classes.fullRow}>
          <Typography variant="subtitle1">Observaciones</Typography>
          <TextField
            variant="outlined"
            multiline
            rows={3}
            fullWidth
            value={formData.observaciones}
            onChange={(e) =>
              setFormData({ ...formData, observaciones: e.target.value })
            }
            disabled={!puedeEditar}
          />
        </div>

        {puedeAprobar && (
          <div className={classes.fullRow}>
            <Typography variant="subtitle1">Tipo de orden de compra</Typography>
            <Select
              variant="outlined"
              fullWidth
              value={formData.tipoOrdenDeCompra || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({ ...prev, tipoOrdenDeCompra: value }));
                setErrors((prev) => {
                  if (!prev.tipoOrdenDeCompra) return prev;
                  const nextErrors = { ...prev };
                  delete nextErrors.tipoOrdenDeCompra;
                  return nextErrors;
                });
              }}
              error={!!errors.tipoOrdenDeCompra}
            >
              <MenuItem value="">Seleccionar tipo</MenuItem>
              <MenuItem value="caja chica">Caja Chica</MenuItem>
              <MenuItem value="expediente">Expediente</MenuItem>
            </Select>
            {errors.tipoOrdenDeCompra && (
              <Typography color="error" variant="body2">
                {errors.tipoOrdenDeCompra}
              </Typography>
            )}
          </div>
        )}

        {/* Área blanca */}
        <div className={classes.areaBlanca}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="subtitle1">Items</Typography>
            {/* {(!(estado === 3 || estado === 4) || editable) && ( */}
            {puedeEditar && (
              <div>
                {/* {!formData.sector ? (
                <Tooltip
                  title="Se debe seleccionar un sector"
                  classes={{ tooltip: classes.customTooltip }}
                  arrow
                >
                  <span>
                    <BotonGenerico
                      tipo="adjuntar"
                      color="primary"
                      onClick={handleAdjuntoClick}
                      text="Adjuntar"
                      disabled
                    />
                  </span>
                </Tooltip>
              ) : (
                <BotonGenerico
                  tipo="adjuntar"
                  color="primary"
                  onClick={handleAdjuntoClick}
                  text="Adjuntar"
                />
              )}

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".xls, .xlsx, .csv"
              /> */}
                {!formData.sector ? (
                  <Tooltip
                    title={
                      <span style={{ fontSize: "0.8rem" }}>
                        Se debe seleccionar un sector
                      </span>
                    }
                    classes={{ tooltip: classes.customTooltip }}
                    arrow
                  >
                    <span>
                      <BotonGenerico
                        tipo="nuevo"
                        color="primary"
                        onClick={handleNuevoClick}
                        text="Nuevo"
                        disabled
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <BotonGenerico
                    tipo="nuevo"
                    color="primary"
                    onClick={handleNuevoClick}
                    text="Nuevo"
                    disabled={loading}
                  />
                )}
              </div>
            )}
          </div>

          {/* Tabla */}
          {items.length > 0 && (
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {/* {showErrorColumn && (
                      <TableCell align="center" style={{ width: 48 }} />
                    )} */}
                    <TableCell align="center">Rubro</TableCell>
                    <TableCell align="center">Orden de servicio</TableCell>
                    <TableCell align="center">Descripción</TableCell>
                    <TableCell align="center">Opciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow
                      key={index}
                    >
                      {/* Nombre del Rubro */}
                      <TableCell align="center">{item.nombreRubro}</TableCell>

                      {/* Información de las OS asociadas */}
                      <TableCell align="center">
                        {item.informacionOS.map((info, idx) => (
                          <div key={idx}>
                            OS: {info.selectedOs || "-"} — Item: {info.itemName || info.selectedItem || "-"} —
                            Cantidad: {info.cantidad || 0} — Precio unitario: $
                            {info.importeUnitario || 0} — Total: $
                            {(info.importeUnitario || 0) * (info.cantidad || 0)}
                          </div>
                        ))}
                      </TableCell>

                      {/* Descripción */}
                      <TableCell align="center">
                        {item.descripcion || "Sin descripción"}
                      </TableCell>

                      <TableCell align="center">
                        <IconButton onClick={() => handleVerDetalleItem(item, index)}>
                          <SearchIcon color="primary" />
                        </IconButton>
                        {puedeEditar && (
                          <IconButton
                            onClick={() => handleDeleteItem(index)}
                            disabled={loading}
                          >
                            <DeleteIcon color="secondary" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        {/* Área blanca para Archivos Adjuntos (Formato idéntico a Items) */}
        <div className={classes.areaBlanca} style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1">Archivos Adjuntos</Typography>
            {puedeEditar && (
              <div>
                <BotonGenerico
                  tipo="nuevo"
                  color="primary"
                  onClick={handleAdjuntoClick}
                  text="Adjuntar"
                  disabled={loading}
                />
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept={ACCEPT_ADJUNTOS_INPUT}
                />
              </div>
            )}
          </div>

          {/* Tabla de Archivos Adjuntos */}
          {todosLosAdjuntos.length > 0 && (
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Nombre</TableCell>
                    <TableCell align="center" style={{ width: "20%" }}>
                      Tamaño
                    </TableCell>
                    <TableCell align="center" style={{ width: "20%" }}>
                      Opciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todosLosAdjuntos.map((adj) => (
                    <TableRow
                      key={
                        adj.esNuevo
                          ? `nuevo-${adj.idTemp}`
                          : `existente-${adj.idAdjunto || adj.indexOriginal}`
                      }
                    >
                      {/* Nombre del archivo */}
                      <TableCell align="left">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 8,
                          }}
                        >
                          {adj.esNuevo && (
                            <Tooltip
                              title={
                                <span style={{ fontSize: "0.8rem" }}>
                                  Falta guardar para subirlo
                                </span>
                              }
                              arrow
                              placement="top"
                            >
                              <CloudUploadIcon
                                style={{
                                  color: "#f57c00",
                                  fontSize: 18,
                                  cursor: "help",
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          )}
                          <DescriptionIcon
                            color="primary"
                            fontSize="small"
                            style={{ flexShrink: 0 }}
                          />
                          <span style={{ wordBreak: "break-all" }}>
                            {adj.nombreOriginal}
                          </span>
                        </div>
                      </TableCell>

                      {/* Tamaño */}
                      <TableCell align="center">
                        {adj.tamano ? formatFileSize(adj.tamano) : "-"}
                      </TableCell>

                      {/* Opciones (Ver y Eliminar) */}
                      <TableCell align="center">
                        <Tooltip
                          title={
                            <span style={{ fontSize: "0.8rem" }}>Ver</span>
                          }
                          arrow
                        >
                          <IconButton onClick={() => handleVerArchivo(adj)}>
                            <SearchIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        {puedeEditar && (
                          <Tooltip
                            title={
                              <span style={{ fontSize: "0.8rem" }}>
                                Eliminar
                              </span>
                            }
                            arrow
                          >
                            <IconButton
                              onClick={() => handleSolicitarEliminarArchivo(adj)}
                              disabled={loading || eliminandoArchivo}
                            >
                              <DeleteIcon color="secondary" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        {openItemsModal && (
          <IngresarItemsModal
            ref={modalRef}
            open={openItemsModal}
            onClose={handleCloseItemsModal}
            onSabeItem={handleSaveItem}
            sectorSelected={formData.sector}
            // limpiarItems={() => setItems([])}
            initialItem={itemModalData}
            readOnly={itemModalReadOnly}
            canEnableEdit={itemModalCanEnableEdit}
          />
        )}
      </DialogContent>
      <DialogFooter>
        {/* Botones de acción */}
        {estado === 1 && items.length > 0 && (
          <>
            {/* {[1, 3].includes(usuarioLocal?.es_admin) && ( */}
            <BotonGenerico
              tipo="guardar"
              color="primary"
              onClick={() => handleGuardar(2)}
              text="Enviar a Aprobación"
              disabled={loading}
            />
            {/* )} */}
            <div className={classes.footerButtons}>
              <BotonGenerico
                tipo="guardar"
                color="primary"
                onClick={() => handleGuardar(1)}
                text="Guardar Borrador"
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* {estado === 2 && !editable && ( */}
        {puedeAprobar && (
          <>
            <BotonGenerico
              tipo="guardar"
              color="primary"
              onClick={() => {
                if (!formData.tipoOrdenDeCompra) {
                  setErrors((prev) => ({
                    ...prev,
                    tipoOrdenDeCompra: "Debe seleccionar si la compra es Caja Chica o Expediente.",
                  }));
                  return;
                }

                handleUpdateEstado(3);
              }}
              text="Aprobar"
              disabled={loading}
            />
            <div className={classes.footerButtons}>
              <BotonGenerico
                tipo="rechazar"
                color="secondary"
                onClick={() => handleUpdateEstado(4)}
                text="Rechazar"
                disabled={loading}
              />
            </div>
          </>
        )}

        {estado === 2 && editable && canEnableApprovalEdit && (
          <>
            <BotonGenerico
              tipo="guardar"
              text="Guardar edición"
              onClick={() => handleGuardar(2)}
              disabled={loading}
            />
          </>
        )}
      </DialogFooter>

      {/* Modal de confirmación para eliminar archivo adjunto */}
      <ConfirmDialog
        open={Boolean(archivoAEliminar)}
        onClose={() => setArchivoAEliminar(null)}
        onConfirm={handleConfirmarEliminarArchivo}
        title="Eliminar archivo adjunto"
        description={`¿Está seguro de que desea eliminar el archivo "${archivoAEliminar?.nombreOriginal || 'adjunto'}"? ${archivoAEliminar?.esNuevo ? 'Se descartará de los archivos a subir.' : 'Se quitará de la requisición y se eliminará físicamente del servidor al guardar los cambios.'}`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </Dialog>
  );
};

export default CrearRequisicionModal;
